/**
 * T26 — Round-end recap (ROUND_RECAP phase) — BODY ONLY (PR-3 D3 통합).
 *
 * The shared top frame (titlebar + RoundInfo + TopBanner rail in READY mode) is
 * rendered by GameScreen via <HorrorTopFrame>; this component owns only the
 * stage body + footer:
 *   ① "N라운드 종료" 선언 → board(매치와 동일 RaceBoardView, pawns FLIP) → zone
 *   2단(인게임 ZoneBoard + 정산 row 강조/↑이동) → ⑤ "N라운드 완료" 선언 → footer.
 *
 * Per-player 확인 상태는 상단 레일 도트(레디 모드 ✓ 칩)로 흡수됐으므로 이 뷰의
 * 자체 헤더 / 자체 TurnRail / ConfirmTracker 칩은 모두 제거. 다음 라운드 진입
 * 시 레일 순서 전환은 페이즈 자연 전환(advance_round 후 ROUND_DRAW_AND_PLACE)
 * 으로 처리 — 별도 FLIP 프리뷰는 생략(폴리시 항목, 후속 옵션).
 *
 * 서버 권위 게이트: 호스트는 모두 confirm한 뒤에만 advance_round 제출.
 * Design + testid contract: docs/wireframes/horror-race/states/round-recap.html.
 */

import type {
  Character,
  Player,
  RoundResolution,
  Zone,
} from "@cloudnest/horror-race-engine";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ROUND_RECAP_CONFIG,
  ROUND_RECAP_SPEED,
  ROUND_RECAP_SPEED_SCALE,
} from "../recap-config";
import { ConfirmFooter } from "./board/ConfirmGate";
import { RaceBoardView } from "./board/RaceBoardView";
import { ZoneBoard } from "./ZoneBoard";
import {
  ZoneSettlementRow,
  type ZoneSettlementState,
} from "./ZoneSettlementRow";

const SCALE = ROUND_RECAP_SPEED_SCALE[ROUND_RECAP_SPEED];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms * SCALE));

function doneStatesForSteps(
  steps: RoundResolution["steps"],
): Record<Zone, ZoneSettlementState> {
  const done: Record<Zone, ZoneSettlementState> = {
    1: "pending",
    2: "pending",
    4: "pending",
  };
  for (const step of steps) {
    done[step.zone] = "done";
  }
  return done;
}

interface RoundRecapViewProps {
  readonly resolution: RoundResolution;
  readonly players: readonly Player[];
  /** Post-resolution (final) pawn positions — pre-move is derived per step. */
  readonly characterPositions: Readonly<Record<Character, number>>;
  readonly trackLength: number;
  /** Live zone placements (intentionally NOT cleared on resolve-without-finisher
   * — see resolve-round.applier). Drives the placement-board read-only view. */
  readonly zones: Readonly<Record<Zone, readonly Character[]>>;
  readonly myParticipantId: string | null;
  /** Observers watch the recap but have no 확인 button. */
  readonly readOnly?: boolean;
  readonly onConfirm: () => void;
}

export function RoundRecapView({
  resolution,
  players,
  characterPositions,
  trackLength,
  zones,
  myParticipantId,
  readOnly = false,
  onConfirm,
}: RoundRecapViewProps) {
  // Pre-move positions: the final board, with each winning pawn rewound to where
  // it started this round so the animation can play it forward.
  const prePositions: Record<Character, number> = { ...characterPositions };
  for (const step of resolution.steps) {
    if (step.winner !== null) prePositions[step.winner] = step.fromPosition;
  }

  const [positions, setPositions] =
    useState<Record<Character, number>>(prePositions);
  const [zoneStates, setZoneStates] = useState<
    Record<Zone, ZoneSettlementState>
  >({
    1: "pending",
    2: "pending",
    4: "pending",
  });
  const [showEnd, setShowEnd] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [animDone, setAnimDone] = useState(false);
  const finishesRace = resolution.steps.some((step) => step.finished);

  // Client-driven presentation timeline. One run per round (keyed on round).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally one run per recap
  useEffect(() => {
    // Reset to this round's starting state first — the component may stay mounted
    // across consecutive recaps, and without this the 확인 button
    // (animDone) would carry over from the previous round before the new
    // animation plays.
    setPositions(prePositions);
    setZoneStates({ 1: "pending", 2: "pending", 4: "pending" });
    setShowEnd(false);
    setShowDone(false);
    setAnimDone(false);

    let cancelled = false;

    async function play() {
      if (prefersReducedMotion()) {
        setPositions(characterPositions);
        setZoneStates(doneStatesForSteps(resolution.steps));
        setShowDone(true);
        setAnimDone(true);
        return;
      }

      // ① round-end declaration
      setShowEnd(true);
      await wait(ROUND_RECAP_CONFIG.declareEndMs);
      if (cancelled) return;
      setShowEnd(false);

      // ②③④ sequential zone resolution
      for (let i = 0; i < resolution.steps.length; i++) {
        const step = resolution.steps[i];
        setZoneStates((prev) => ({ ...prev, [step.zone]: "active" }));
        await wait(ROUND_RECAP_CONFIG.zoneHighlightMs);
        if (cancelled) return;
        if (step.winner !== null) {
          const winner = step.winner;
          setPositions((prev) => ({ ...prev, [winner]: step.toPosition }));
          await wait(
            ROUND_RECAP_CONFIG.pawnMoveMs + ROUND_RECAP_CONFIG.zoneSettleMs,
          );
        } else {
          await wait(ROUND_RECAP_CONFIG.noMoveMs);
        }
        if (cancelled) return;
        setZoneStates((prev) => ({ ...prev, [step.zone]: "done" }));
      }

      // ⑤ round-complete declaration → enable 확인
      setShowDone(true);
      await wait(ROUND_RECAP_CONFIG.declareDoneMs);
      if (cancelled) return;
      setAnimDone(true);
    }

    void play();
    return () => {
      cancelled = true;
    };
  }, [resolution.round]);

  const confirmedCount = players.filter((p) => p.hasConfirmedRoundRecap).length;
  const allConfirmed = confirmedCount === players.length;
  const myPlayer = players.find((p) => p.id === myParticipantId);
  const iConfirmed = myPlayer?.hasConfirmedRoundRecap ?? false;

  return (
    <>
      {/* STAGE BODY — declarations + animated board + zone 2-row */}
      <div className="relative flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        <Declaration
          show={showEnd}
          variant="end"
          text={`${resolution.round}라운드 종료`}
        />

        <RecapBoard positions={positions} trackLength={trackLength} />

        {/* Zone area — ① in-game ZoneBoard(read-only placements) ② settlement
            row(winners with emphasis + rise animation). Columns align. */}
        <div className="flex flex-col gap-1.5">
          <div data-testid="round-recap-zoneboard">
            <ZoneBoard zones={zones} />
          </div>
          <ZoneSettlementRow steps={resolution.steps} zoneStates={zoneStates} />
        </div>

        <Declaration
          show={showDone}
          variant="done"
          text={
            finishesRace
              ? `${resolution.round}라운드 완료 · 레이스 종료`
              : `${resolution.round}라운드 완료`
          }
        />
      </div>

      <ConfirmFooter
        readOnly={readOnly}
        allConfirmed={allConfirmed}
        iConfirmed={iConfirmed}
        enabled={animDone}
        pendingLabel="연출 재생 중…"
        advanceMsg={
          finishesRace
            ? "모든 플레이어가 확인했습니다 — 세트 정산으로 이동합니다…"
            : "모든 플레이어가 확인했습니다 — 다음 라운드를 시작합니다…"
        }
        onConfirm={onConfirm}
        btnTestId="round-recap-confirm-btn"
        advanceMsgTestId="round-recap-advance-msg"
      />
    </>
  );
}

// ── round-end / round-complete declaration banner ─────────────────────────
function Declaration({
  show,
  variant,
  text,
}: {
  readonly show: boolean;
  readonly variant: "end" | "done";
  readonly text: string;
}) {
  if (!show) return null;
  return (
    <div
      data-testid={
        variant === "end" ? "round-recap-declare" : "round-recap-declare-done"
      }
      className="rounded-xl p-2 text-center font-extrabold text-lg"
      style={{
        color:
          variant === "end"
            ? "var(--cn-horror-round-text)"
            : "var(--cn-horror-finished-flash)",
        textShadow:
          variant === "done" ? "0 0 14px rgba(255,215,0,0.4)" : "none",
      }}
    >
      {text}
    </div>
  );
}

// ── animated race board — reuses the in-game <RaceBoardView> (셀 그리드) so the
// recap shows the SAME board as the match. A pawn slides from its old cell to
// the new one via FLIP (exact cell alignment, no left% drift). Pawns are re-keyed
// by cell across renders, so they are matched by data-character. Cross-round is
// seamless: round N+1's start positions equal round N's final, so the FLIP sees
// no delta at the round boundary.
function RecapBoard({
  positions,
  trackLength,
}: {
  readonly positions: Readonly<Record<Character, number>>;
  readonly trackLength: number;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const prevRects = useRef(new Map<string, DOMRect>());
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure each position change
  useLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    const board = boardRef.current;
    if (!board) return;
    for (const el of board.querySelectorAll<HTMLElement>(
      '[data-testid="round-recap-pawn"]',
    )) {
      const character = el.dataset.character;
      if (!character) continue;
      const next = el.getBoundingClientRect();
      const prev = prevRects.current.get(character);
      if (prev) {
        const dx = prev.left - next.left;
        const dy = prev.top - next.top;
        if (dx || dy) {
          el.animate(
            [
              { transform: `translate(${dx}px, ${dy}px)` },
              { transform: "translate(0, 0)" },
            ],
            {
              duration: ROUND_RECAP_CONFIG.pawnMoveMs * SCALE,
              easing: "cubic-bezier(.34,1.2,.5,1)",
            },
          );
        }
      }
      prevRects.current.set(character, next);
    }
  }, [positions]);

  return (
    <div ref={boardRef} data-testid="round-recap-board">
      <RaceBoardView
        positions={positions}
        trackLength={trackLength}
        laneTestId="round-recap-lane"
        pawnTestId="round-recap-pawn"
      />
    </div>
  );
}
